import { useState, useMemo, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import SASidebar from '@/pages/sa/dashboard/components/SASidebar';
import {
  legalTemplates as mockTemplates,
  type LegalTemplate,
  type LegalTemplateVersion,
  TYPE_DEFINITIONS,
  getActiveVersion,
} from '@/mocks/superAdminLegalTemplates';
import TemplateCard from './components/TemplateCard';
import TemplateEditorModal from './components/TemplateEditorModal';
import TemplatePreviewModal from './components/TemplatePreviewModal';
import TemplateHistoryModal from './components/TemplateHistoryModal';

type LoadingState = 'loading' | 'ready' | 'error';

function simulateLoad(): Promise<LegalTemplate[]> {
  return new Promise((resolve, reject) => {
    const delay = 600 + Math.random() * 400;
    setTimeout(() => {
      if (Math.random() > 0.93) {
        reject(new Error('Error al cargar las plantillas legales. Verificá tu conexión.'));
      } else {
        resolve(mockTemplates);
      }
    }, delay);
  });
}

const typeKeys = ['todos', ...Object.keys(TYPE_DEFINITIONS)] as const;
type TypeFilter = (typeof typeKeys)[number];

export default function SuperAdminLegalTemplates() {
  const [templates, setTemplates] = useState<LegalTemplate[]>([]);
  const [loadingState, setLoadingState] = useState<LoadingState>('loading');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [activeType, setActiveType] = useState<TypeFilter>('todos');
  const [searchTerm, setSearchTerm] = useState('');

  // Modal states
  const [editTemplate, setEditTemplate] = useState<LegalTemplate | null>(null);
  const [previewTemplate, setPreviewTemplate] = useState<LegalTemplate | null>(null);
  const [historyTemplate, setHistoryTemplate] = useState<LegalTemplate | null>(null);

  const loadData = useCallback(() => {
    setLoadingState('loading');
    setErrorMsg(null);
    simulateLoad()
      .then((data) => {
        setTemplates(data);
        setLoadingState('ready');
      })
      .catch((err) => {
        setErrorMsg(err.message || 'Error desconocido');
        setLoadingState('error');
      });
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Stats
  const stats = useMemo(() => {
    const total = templates.length;
    const active = templates.filter((t) => t.isActive).length;
    const hasDraft = templates.filter((t) => t.versions.some((v) => v.status === 'draft')).length;
    const inactive = templates.filter((t) => !t.isActive).length;
    return { total, active, hasDraft, inactive };
  }, [templates]);

  // Type counts
  const typeCounts = useMemo(() => {
    const counts: Record<string, number> = { todos: templates.length };
    Object.keys(TYPE_DEFINITIONS).forEach((tk) => {
      counts[tk] = templates.filter((t) => t.typeKey === tk).length;
    });
    return counts;
  }, [templates]);

  // Filtered
  const filteredTemplates = useMemo(() => {
    let result = templates;
    if (activeType !== 'todos') {
      result = result.filter((t) => t.typeKey === activeType);
    }
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      result = result.filter(
        (t) =>
          t.typeLabel.toLowerCase().includes(q) ||
          t.versions.some(
            (v) =>
              v.title.toLowerCase().includes(q) ||
              v.content.toLowerCase().includes(q),
          ),
      );
    }
    return result;
  }, [templates, activeType, searchTerm]);

  const showToast = (icon: 'success' | 'error' | 'warning' | 'info', title: string) => {
    Swal.fire({
      icon,
      title,
      toast: true,
      position: 'top-end',
      showConfirmButton: false,
      timer: 3000,
      timerProgressBar: true,
      customClass: {
        popup: 'rounded-2xl text-sm',
        title: 'text-xs font-semibold',
      },
    });
  };

  // --- Handlers ---

  const handleSaveVersion = (templateId: number, newVersion: LegalTemplateVersion) => {
    setTemplates((prev) =>
      prev.map((t) =>
        t.id === templateId
          ? {
              ...t,
              currentVersion: newVersion.versionNumber,
              versions: [...t.versions, newVersion],
              updatedAt: new Date().toISOString(),
            }
          : t,
      ),
    );
  };

  const handleActivateVersion = (templateId: number, versionNumber: number) => {
    Swal.fire({
      icon: 'question',
      title: '¿Activar esta versión?',
      html: `
        <div style="text-align:left;font-size:13px">
          <p style="margin-bottom:8px">Estás por activar la <strong>v${versionNumber}</strong> como versión vigente.</p>
          <p style="color:#f97316;font-size:12px">La versión anterior será archivada automáticamente. Esta acción afecta los nuevos contratos que se generen.</p>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: 'Activar Versión',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#10b981',
      cancelButtonColor: '#d1d5db',
      customClass: {
        popup: 'rounded-2xl text-sm',
        title: 'text-sm font-bold',
        confirmButton: 'text-xs font-medium px-4 py-2 rounded-xl',
        cancelButton: 'text-xs font-medium px-4 py-2 rounded-xl text-foreground-700',
      },
      buttonsStyling: false,
    }).then((result) => {
      if (result.isConfirmed) {
        setTemplates((prev) =>
          prev.map((t) => {
            if (t.id !== templateId) return t;
            return {
              ...t,
              currentVersion: versionNumber,
              isActive: true,
              updatedAt: new Date().toISOString(),
              versions: t.versions.map((v) => {
                if (v.versionNumber === versionNumber) return { ...v, status: 'active' as const };
                if (v.status === 'active') return { ...v, status: 'archived' as const };
                return v;
              }),
            };
          }),
        );
        setHistoryTemplate(null);
        Swal.fire({
          icon: 'success',
          title: 'Versión activada',
          html: `<p style="font-size:13px">La <strong>v${versionNumber}</strong> ahora es la versión vigente. Los contratos nuevos usarán esta plantilla.</p>`,
          confirmButtonColor: '#1a1a1a',
          customClass: {
            popup: 'rounded-2xl text-sm',
            title: 'text-sm font-bold',
            confirmButton: 'text-xs font-medium px-4 py-2 rounded-xl',
          },
        });
      }
    });
  };

  const handleArchiveVersion = (templateId: number, versionNumber: number) => {
    Swal.fire({
      icon: 'warning',
      title: '¿Archivar versión?',
      text: `La v${versionNumber} será archivada. Esta acción no se puede deshacer.`,
      showCancelButton: true,
      confirmButtonText: 'Archivar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#78716c',
      cancelButtonColor: '#d1d5db',
      customClass: {
        popup: 'rounded-2xl text-sm',
        title: 'text-sm font-bold',
        confirmButton: 'text-xs font-medium px-4 py-2 rounded-xl',
        cancelButton: 'text-xs font-medium px-4 py-2 rounded-xl text-foreground-700',
      },
      buttonsStyling: false,
    }).then((result) => {
      if (result.isConfirmed) {
        setTemplates((prev) =>
          prev.map((t) => {
            if (t.id !== templateId) return t;
            return {
              ...t,
              updatedAt: new Date().toISOString(),
              versions: t.versions.map((v) =>
                v.versionNumber === versionNumber ? { ...v, status: 'archived' as const } : v,
              ),
            };
          }),
        );
        setHistoryTemplate(null);
        showToast('success', `Versión v${versionNumber} archivada.`);
      }
    });
  };

  const handleDuplicate = (template: LegalTemplate) => {
    Swal.fire({
      icon: 'question',
      title: '¿Duplicar plantilla?',
      html: `
        <div style="text-align:left;font-size:13px">
          <p style="margin-bottom:8px">Se creará una copia completa de <strong>${template.typeLabel}</strong> como nueva versión borrador.</p>
          <p style="color:#6b7280;font-size:12px">La copia incluirá todo el contenido actual. Vas a poder editarla libremente.</p>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: 'Duplicar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#1a1a1a',
      cancelButtonColor: '#d1d5db',
      customClass: {
        popup: 'rounded-2xl text-sm',
        title: 'text-sm font-bold',
        confirmButton: 'text-xs font-medium px-4 py-2 rounded-xl',
        cancelButton: 'text-xs font-medium px-4 py-2 rounded-xl text-foreground-700',
      },
      buttonsStyling: false,
    }).then((result) => {
      if (result.isConfirmed) {
        const active = getActiveVersion(template);
        const newVersion: LegalTemplateVersion = {
          versionNumber: template.currentVersion + 1,
          title: `${active.title} (Copia)`,
          content: active.content,
          status: 'draft',
          createdAt: new Date().toISOString(),
          createdBy: 'Admin Global',
          notes: 'Duplicado desde v' + active.versionNumber,
        };
        setTemplates((prev) =>
          prev.map((t) =>
            t.id === template.id
              ? {
                  ...t,
                  currentVersion: newVersion.versionNumber,
                  versions: [...t.versions, newVersion],
                  updatedAt: new Date().toISOString(),
                }
              : t,
          ),
        );
        showToast('success', `Plantilla duplicada como v${newVersion.versionNumber} (borrador).`);
      }
    });
  };

  return (
    <div className="min-h-screen bg-background-100">
      <div className="flex">
        <SASidebar />
        <main className="flex-1 min-w-0">
          <div className="p-4 md:p-6 lg:p-8 pb-8 space-y-5 max-w-[1600px] mx-auto">
            {/* Breadcrumbs */}
            <div className="flex items-center gap-1.5 text-[10px] text-foreground-400">
              <Link to="/sa/dashboard" className="hover:text-foreground-600 transition-colors cursor-pointer">Dashboard</Link>
              <i className="ri-arrow-right-s-line text-[9px]"></i>
              <span className="text-foreground-500 font-medium">Legal Templates</span>
            </div>

            {/* Page header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h2 className="text-foreground-950 font-bold text-base">Plantillas Legales</h2>
                <p className="text-foreground-400 text-xs mt-0.5">
                  {loadingState === 'ready'
                    ? `${stats.total} plantillas · ${stats.active} activas · ${stats.hasDraft} con borradores`
                    : 'Cargando plantillas...'}
                </p>
              </div>
              <div className="relative w-full sm:w-56">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-xs"></i>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Buscar plantilla..."
                  disabled={loadingState !== 'ready'}
                  className="w-full pl-8 pr-8 py-2 bg-background-50 border border-background-200 rounded-xl text-xs text-foreground-800 placeholder:text-foreground-300 outline-none focus:border-foreground-300 transition-colors disabled:opacity-50"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 w-5 h-5 rounded-md bg-background-200 flex items-center justify-center cursor-pointer hover:bg-background-300 transition-colors"
                  >
                    <i className="ri-close-line text-foreground-500 text-[10px]"></i>
                  </button>
                )}
              </div>
            </div>

            {/* Stats KPI */}
            {loadingState === 'ready' && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {[
                  { label: 'Total Plantillas', value: stats.total, icon: 'ri-file-text-line', color: 'text-foreground-600', bg: 'bg-background-200' },
                  { label: 'Activas', value: stats.active, icon: 'ri-check-double-line', color: 'text-emerald-500', bg: 'bg-emerald-50' },
                  { label: 'Con Borradores', value: stats.hasDraft, icon: 'ri-draft-line', color: 'text-amber-500', bg: 'bg-amber-50' },
                  { label: 'Inactivas', value: stats.inactive, icon: 'ri-archive-line', color: 'text-stone-500', bg: 'bg-stone-50' },
                ].map((stat) => (
                  <div key={stat.label} className="bg-background-50 border border-background-200 rounded-xl p-3 flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-lg ${stat.bg} flex items-center justify-center shrink-0`}>
                      <i className={`${stat.icon} ${stat.color} text-sm`}></i>
                    </div>
                    <div>
                      <p className="text-foreground-400 text-[10px] font-medium whitespace-nowrap">{stat.label}</p>
                      <p className="text-foreground-800 text-sm font-bold">{stat.value}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Loading skeletons */}
            {loadingState === 'loading' && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="bg-background-50 border border-background-200 rounded-xl p-3 flex items-center gap-3 animate-pulse">
                    <div className="w-9 h-9 rounded-lg bg-background-200"></div>
                    <div className="space-y-1">
                      <div className="w-14 h-2.5 bg-background-200 rounded"></div>
                      <div className="w-8 h-3 bg-background-200 rounded"></div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Info banner */}
            {loadingState === 'ready' && (
              <div className="bg-background-50 border border-background-200 rounded-2xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-background-100 flex items-center justify-center shrink-0">
                    <i className="ri-scales-3-line text-foreground-500 text-lg"></i>
                  </div>
                  <div>
                    <p className="text-foreground-800 text-xs font-semibold">Sistema de Plantillas Legales Dinámicas</p>
                    <p className="text-foreground-400 text-[10px]">
                      Editá los textos legales sin tocar código. Las variables {'{{variable}}'} se reemplazan automáticamente con los datos de cada radio y contrato. Los cambios se guardan como nuevas versiones.
                    </p>
                  </div>
                </div>
                <span className="px-2.5 py-1 rounded-full bg-background-200 text-foreground-500 text-[10px] font-medium whitespace-nowrap flex items-center gap-1 self-start sm:self-auto">
                  <i className="ri-information-line text-[9px]"></i>
                  Modo simulación
                </span>
              </div>
            )}

            {/* Type filter chips */}
            {loadingState === 'ready' && (
              <div className="flex items-center gap-1.5 flex-wrap">
                {typeKeys.map((tk) => {
                  const def = tk === 'todos' ? { label: 'Todos', icon: 'ri-stack-line' } : TYPE_DEFINITIONS[tk];
                  return (
                    <button
                      key={tk}
                      onClick={() => setActiveType(tk)}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-medium transition-all duration-200 cursor-pointer whitespace-nowrap border
                        ${activeType === tk
                          ? 'bg-foreground-950 text-background-50 border-foreground-950'
                          : 'bg-background-50 text-foreground-600 border-background-200 hover:border-foreground-300'
                        }`}
                    >
                      <i className={`${def.icon} text-[10px]`}></i>
                      {def.label}
                      <span className={`text-[9px] ${activeType === tk ? 'text-background-50/70' : 'text-foreground-300'}`}>
                        {typeCounts[tk] ?? 0}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}

            {/* Error */}
            {loadingState === 'error' && (
              <div className="bg-background-50 border border-background-200 rounded-2xl p-12 text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-red-50 flex items-center justify-center">
                  <i className="ri-error-warning-line text-red-400 text-2xl"></i>
                </div>
                <p className="text-foreground-800 text-sm font-semibold mb-1">Error al cargar plantillas</p>
                <p className="text-foreground-400 text-xs mb-4">{errorMsg}</p>
                <button
                  onClick={loadData}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-foreground-950 text-background-50 text-xs font-bold hover:bg-foreground-800 transition-colors cursor-pointer"
                >
                  <i className="ri-refresh-line text-sm"></i>
                  Reintentar
                </button>
              </div>
            )}

            {/* Empty */}
            {loadingState === 'ready' && filteredTemplates.length === 0 && (
              <div className="bg-background-50 border border-background-200 rounded-2xl p-16 text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-background-100 flex items-center justify-center">
                  <i className="ri-file-search-line text-foreground-400 text-2xl"></i>
                </div>
                <p className="text-foreground-600 text-sm font-semibold mb-1">Sin resultados</p>
                <p className="text-foreground-400 text-xs">
                  No hay plantillas que coincidan con los filtros actuales.
                </p>
              </div>
            )}

            {/* Template grid */}
            {loadingState === 'ready' && filteredTemplates.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {filteredTemplates.map((tpl) => (
                  <TemplateCard
                    key={tpl.id}
                    template={tpl}
                    onPreview={setPreviewTemplate}
                    onEdit={setEditTemplate}
                    onHistory={setHistoryTemplate}
                    onDuplicate={handleDuplicate}
                    onActivate={(t) => {
                      const draft = t.versions.find((v) => v.status === 'draft');
                      if (draft) {
                        handleActivateVersion(t.id, draft.versionNumber);
                      }
                    }}
                  />
                ))}
              </div>
            )}

            {/* Variables reference */}
            {loadingState === 'ready' && (
              <div className="bg-background-50 border border-background-200 rounded-2xl p-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-9 h-9 rounded-xl bg-background-100 flex items-center justify-center">
                    <i className="ri-code-s-slash-line text-foreground-500 text-base"></i>
                  </div>
                  <div>
                    <p className="text-foreground-800 text-xs font-semibold">Variables Dinámicas Disponibles</p>
                    <p className="text-foreground-400 text-[10px]">Usá estas variables en el contenido. Se reemplazan automáticamente.</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                  {[
                    { var: '{{radio_name}}', label: 'Nombre de la Radio', ex: 'Radio Estelar 104.5' },
                    { var: '{{frequency_web}}', label: 'Frecuencia RFW', ex: '104.5 RFW' },
                    { var: '{{plan_name}}', label: 'Nombre del Plan', ex: 'Pro' },
                    { var: '{{monthly_price}}', label: 'Precio Mensual', ex: '$19.99 USD' },
                    { var: '{{annual_license_name}}', label: 'Licencia Anual', ex: 'Licencia Anual de Frecuencia RFW' },
                    { var: '{{annual_license_amount}}', label: 'Monto Licencia', ex: '$49.95 USD' },
                    { var: '{{storage_limit}}', label: 'Storage', ex: '50 GB' },
                    { var: '{{contract_date}}', label: 'Fecha Contrato', ex: '01 Jul 2026' },
                    { var: '{{renewal_date}}', label: 'Renovación', ex: '01 Jul 2027' },
                    { var: '{{representative_name}}', label: 'Representante', ex: 'Carlos Mendoza' },
                    { var: '{{company_name}}', label: 'Empresa', ex: 'Estelar Media Group' },
                  ].map((v) => (
                    <div key={v.var} className="flex items-start gap-2 p-2 rounded-lg bg-background-100 border border-background-200/70">
                      <code className="text-foreground-600 text-[10px] font-mono font-bold shrink-0">{v.var}</code>
                      <div className="min-w-0">
                        <p className="text-foreground-700 text-[10px] font-medium leading-tight">{v.label}</p>
                        <p className="text-foreground-300 text-[9px] truncate">{v.ex}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Bottom legal notice */}
            {loadingState === 'ready' && (
              <div className="bg-amber-50/60 border border-amber-200 rounded-2xl p-4 flex items-start gap-3">
                <div className="w-9 h-9 rounded-xl bg-amber-100 flex items-center justify-center shrink-0 mt-0.5">
                  <i className="ri-alert-line text-amber-500 text-base"></i>
                </div>
                <div>
                  <p className="text-amber-800 text-xs font-semibold">Documentos Legales Vinculantes</p>
                  <p className="text-amber-600/80 text-[10px] leading-relaxed">
                    Las plantillas aquí administradas constituyen documentos legales vinculantes. Cualquier modificación debe ser revisada por asesoría legal. La versión activa de cada plantilla es la que se aplica a todos los contratos y relaciones con la Plataforma. El historial completo de versiones se conserva para fines de auditoría.
                  </p>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Modals */}
      <TemplateEditorModal
        template={editTemplate}
        onClose={() => setEditTemplate(null)}
        onSave={handleSaveVersion}
      />

      <TemplatePreviewModal
        template={previewTemplate}
        onClose={() => setPreviewTemplate(null)}
      />

      <TemplateHistoryModal
        template={historyTemplate}
        onClose={() => setHistoryTemplate(null)}
        onActivateVersion={handleActivateVersion}
        onArchiveVersion={handleArchiveVersion}
      />

      {/* Mobile bottom hint */}
      <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-background-50/98 backdrop-blur-2xl border-t border-background-200 py-3 px-4 safe-bottom">
        <p className="text-center text-foreground-400 text-[10px] font-medium">
          Super Admin · Legal Templates
        </p>
      </div>
    </div>
  );
}